import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Ban, CheckCircle, BarChart2, Mail, Tag, Store, Newspaper, Share2, Users, ShieldCheck, Flag, Cpu, HelpCircle, Database, GitBranch, Activity, Zap, Layers, Building2, FileText, UserSearch, Sparkles, SpellCheck, Gauge, Fingerprint, MapPin, KeyRound, Globe } from "lucide-react";
import { getPendingNewsCount } from "interfaces/restaurantNews";
import { getFranchiseReviewCounts } from "interfaces/events";

const NEWS_SEEN_COUNT_KEY = "bo_news_seen_count";
const FRANCHISE_REVIEW_SEEN_COUNT_KEY = "bo_franchise_review_seen_count";

function isBackOfficePath(pathname: string): boolean {
    return pathname === "/bo" || pathname.startsWith("/bo/") || pathname === "/bo-commercial" || pathname.startsWith("/bo-commercial/");
}

export default function BONavigation() {
    const navigate = useNavigate();
    const location = useLocation();
    const [pendingNewsCount, setPendingNewsCount] = useState(0);
    const [seenNewsCount, setSeenNewsCount] = useState(() => Number(localStorage.getItem(NEWS_SEEN_COUNT_KEY) ?? 0));
    const [pendingFranchiseReviewCount, setPendingFranchiseReviewCount] = useState(0);
    const [seenFranchiseReviewCount, setSeenFranchiseReviewCount] = useState(() => Number(localStorage.getItem(FRANCHISE_REVIEW_SEEN_COUNT_KEY) ?? 0));

    // This tab bar renders nothing on the back-office pages (BackOfficeNavigation, in the admin
    // layout, replaced it and fetches the same two badges) — yet it used to request both counts
    // on every page that still mounts it. The franchise count reads every review event, so each
    // BO page view cost two heavy requests for nothing.
    const hidden = isBackOfficePath(location.pathname);
    useEffect(() => {
        if (hidden) return;
        getPendingNewsCount().then(setPendingNewsCount).catch(() => {});
        getFranchiseReviewCounts().then((c) => setPendingFranchiseReviewCount(c.pending)).catch(() => {});
    }, [hidden]);

    const unseenNewsCount = Math.max(0, pendingNewsCount - seenNewsCount);
    const unseenFranchiseReviewCount = Math.max(0, pendingFranchiseReviewCount - seenFranchiseReviewCount);

    const handleNewsClick = () => {
        localStorage.setItem(NEWS_SEEN_COUNT_KEY, String(pendingNewsCount));
        setSeenNewsCount(pendingNewsCount);
        navigate("/bo/news-articles");
    };

    const handleFranchiseReviewClick = () => {
        localStorage.setItem(FRANCHISE_REVIEW_SEEN_COUNT_KEY, String(pendingFranchiseReviewCount));
        setSeenFranchiseReviewCount(pendingFranchiseReviewCount);
        navigate("/bo/franchise-review");
    };

    const manualTabs = [
        {
            id: "newsArticles",
            label: "Actualités",
            icon: Newspaper,
            path: "/bo/news-articles",
            badge: unseenNewsCount,
            onClick: handleNewsClick,
        },
        {
            id: "franchiseReview",
            label: "Franchise Review",
            icon: GitBranch,
            path: "/bo/franchise-review",
            badge: unseenFranchiseReviewCount,
            onClick: handleFranchiseReviewClick,
        },
    ];

    const tabs = [
        {
            id: "userActivity",
            label: "Activité users",
            icon: Activity,
            path: "/bo/activity",
        },
        {
            id: "publicApi",
            label: "API publique",
            icon: Globe,
            path: "/bo/public-api",
        },
        {
            id: "apiKeys",
            label: "Clés API",
            icon: KeyRound,
            path: "/bo/api-keys",
        },
        {
            id: "enrichments",
            label: "To-do",
            icon: CheckCircle,
            path: "/bo/enrichments",
        },
        {
            id: "events",
            label: "Events",
            icon: BarChart2,
            path: "/bo/events",
        },
        {
            id: "emails",
            label: "Email Sender",
            icon: Mail,
            path: "/bo/emails",
        },
        {
            id: "ghosts",
            label: "Fantômes",
            icon: Layers,
            path: "/bo/ghosts",
        },
        {
            id: "todoTags",
            label: "Todo Tags",
            icon: Tag,
            path: "/bo/todo-tags",
        },
        {
            id: "bulkTags",
            label: "Bulk Tag",
            icon: Tag,
            path: "/bo/bulk-tags",
        },
        {
            id: "todoFranchise",
            label: "Bulk Franchise",
            icon: Store,
            path: "/bo/todo-franchise",
        },
        {
            id: "weirdNames",
            label: "Noms bizarres",
            icon: SpellCheck,
            path: "/bo/weird-names",
        },
        {
            id: "franchises",
            label: "Franchises",
            icon: Store,
            path: "/bo/franchises",
        },
        {
            id: "ownershipTree",
            label: "Ownership Tree",
            icon: Share2,
            path: "/bo/ownership-tree",
        },
        {
            id: "founderEnrichment",
            label: "Founder Enrichment",
            icon: UserSearch,
            path: "/bo/founder-enrichment",
        },
        {
            id: "people",
            label: "Personnes",
            icon: Users,
            path: "/bo/people",
        },
        {
            id: "identityRepair",
            label: "Identité dirigeants",
            icon: Fingerprint,
            path: "/bo/identity-repair",
        },
        {
            id: "alimConfiance",
            label: "AlimConfiance",
            icon: ShieldCheck,
            path: "/bo/alim-confiance",
        },
        {
            id: "signalReports",
            label: "Signalements",
            icon: Flag,
            path: "/bo/signal-reports",
        },
        {
            id: "services",
            label: "Services",
            icon: Cpu,
            path: "/bo/services",
        },
        {
            id: "zone",
            label: "Zone",
            icon: MapPin,
            path: "/bo/zone",
        },
        {
            id: "enrichmentStatus",
            label: "Suivi enrichissements",
            icon: Gauge,
            path: "/bo/enrichment-status",
        },
        {
            id: "unknownTags",
            label: "Tags inconnus",
            icon: HelpCircle,
            path: "/bo/unknown-tags",
        },
        {
            id: "banishedTags",
            label: "Tags bannis",
            icon: Ban,
            path: "/bo/banished-tags",
        },
        {
            id: "dbStats",
            label: "KPI",
            icon: Database,
            path: "/bo/db-stats",
        },
        {
            id: "queryLogs",
            label: "Query Logs",
            icon: Activity,
            path: "/bo/query-logs",
        },
        {
            id: "aiPrompts",
            label: "Prompts IA",
            icon: Sparkles,
            path: "/bo/ai-prompts",
        },
        {
            id: "clientTypings",
            label: "Typologies clients",
            icon: UserSearch,
            path: "/bo/client-typings",
        },
        {
            id: "autoApprove",
            label: "Auto-Approve",
            icon: Zap,
            path: "/bo/auto-approve",
        },
        {
            id: "boddacHistory",
            label: "BODACC",
            icon: Layers,
            path: "/bo/boddac-history",
        },
        {
            id: "duplicatePeople",
            label: "Doublons dirigeants",
            icon: UserSearch,
            path: "/bo/duplicate-people",
        },
        {
            id: "importEtablissements",
            label: "Import Établissements",
            icon: Building2,
            path: "/bo/import-etablissements",
        },
        {
            id: "weeklyMail",
            label: "Mail hebdo",
            icon: FileText,
            path: "/bo/weekly-mail",
        },
    ];

    const isActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + "/");
    };

    const renderTab = (tab: {
        id: string;
        label: string;
        icon: React.ElementType;
        path: string;
        badge?: number;
        onClick?: () => void;
    }) => {
        const Icon = tab.icon;
        const active = isActive(tab.path);

        return (
            <button
                key={tab.id}
                onClick={tab.onClick ?? (() => navigate(tab.path))}
                className={`relative py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    active
                        ? "border-black text-black"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
            >
                <span className="relative inline-block mr-2">
                    <Icon className="w-5 h-5" />
                    {!!tab.badge && (
                        <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                            {tab.badge > 99 ? "99+" : tab.badge}
                        </span>
                    )}
                </span>
                {tab.label}
            </button>
        );
    };

    if (hidden) return null;

    return (
        <div className="mb-6">
            <div className="mb-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1 px-1">Traitement manuel</p>
                <nav className="flex flex-wrap gap-x-8 border-b border-gray-200">
                    {manualTabs.map(renderTab)}
                </nav>
            </div>
            <nav className="flex flex-wrap gap-x-8 border-b border-gray-200">
                {tabs.map(renderTab)}
            </nav>
        </div>
    );
}
